import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HandshakeIcon from '../../../components/HandshakeIcon';
import './Policies.css';
import '../../../styles/accessibility.css';

const renderCategoryIcon = (cat, size = 28) => {
  if (cat.id === 'ethics') return <HandshakeIcon size={size} />;
  return cat.icon;
};

const Policies = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const categories = [
    { id: 'all', name: 'All Policies', icon: '📋', count: 31 },
    { id: 'platform', name: 'Platform Policies', icon: '⛓️', count: 7 },
    { id: 'data-privacy', name: 'Data Privacy', icon: '🔒', count: 6 },
    { id: 'security', name: 'Security', icon: '🛡️', count: 5 },
    { id: 'compliance', name: 'Compliance', icon: '✓', count: 4 },
    { id: 'operations', name: 'Operations', icon: '⚙️', count: 5 },
    { id: 'ethics', name: 'Ethics & Conduct', icon: null, count: 4 }
  ];

  const policies = [
    {
      id: 1,
      title: 'Data Protection & Privacy Policy',
      category: 'data-privacy',
      version: '3.2',
      status: 'active',
      lastUpdated: '2025-09-15',
      owner: 'Legal & Compliance Team',
      description: 'Comprehensive guidelines for protecting personal and sensitive data across all operations.',
      effectiveDate: '2025-10-01',
      reviewDate: '2026-04-01',
      priority: 'critical',
      tags: ['GDPR', 'CCPA', 'Data Protection', 'Privacy']
    },
    {
      id: 2,
      title: 'Information Security Standards',
      category: 'security',
      version: '2.8',
      status: 'active',
      lastUpdated: '2025-08-22',
      owner: 'Security Team',
      description: 'Security protocols and standards for protecting organizational information assets.',
      effectiveDate: '2025-09-01',
      reviewDate: '2026-03-01',
      priority: 'critical',
      tags: ['ISO 27001', 'Security', 'Access Control']
    },
    {
      id: 3,
      title: 'Regulatory Compliance Framework',
      category: 'compliance',
      version: '4.1',
      status: 'active',
      lastUpdated: '2025-09-28',
      owner: 'Compliance Team',
      description: 'Framework for ensuring compliance with industry regulations and standards.',
      effectiveDate: '2025-10-05',
      reviewDate: '2026-04-05',
      priority: 'high',
      tags: ['SOX', 'Compliance', 'Audit', 'Regulatory']
    },
    {
      id: 4,
      title: 'Incident Response Protocol',
      category: 'security',
      version: '1.9',
      status: 'active',
      lastUpdated: '2025-07-10',
      owner: 'Security Operations',
      description: 'Procedures for identifying, responding to, and recovering from security incidents.',
      effectiveDate: '2025-08-01',
      reviewDate: '2026-02-01',
      priority: 'critical',
      tags: ['Incident Response', 'Security', 'Emergency']
    },
    {
      id: 5,
      title: 'Code of Business Conduct',
      category: 'ethics',
      version: '5.0',
      status: 'active',
      lastUpdated: '2025-09-01',
      owner: 'Ethics & Compliance',
      description: 'Standards of ethical behavior and business conduct for all employees and stakeholders.',
      effectiveDate: '2025-09-15',
      reviewDate: '2026-09-15',
      priority: 'high',
      tags: ['Ethics', 'Conduct', 'Values', 'Culture']
    },
    {
      id: 6,
      title: 'Access Control Policy',
      category: 'security',
      version: '3.5',
      status: 'active',
      lastUpdated: '2025-08-15',
      owner: 'IT Security',
      description: 'Guidelines for managing and controlling access to systems, applications, and data.',
      effectiveDate: '2025-09-01',
      reviewDate: '2026-03-01',
      priority: 'high',
      tags: ['Access Control', 'IAM', 'Security']
    },
    {
      id: 7,
      title: 'Data Retention & Disposal',
      category: 'data-privacy',
      version: '2.3',
      status: 'active',
      lastUpdated: '2025-07-20',
      owner: 'Data Governance',
      description: 'Policies for data lifecycle management including retention periods and secure disposal.',
      effectiveDate: '2025-08-01',
      reviewDate: '2026-02-01',
      priority: 'medium',
      tags: ['Data Management', 'Retention', 'Disposal']
    },
    {
      id: 8,
      title: 'Third-Party Risk Management',
      category: 'compliance',
      version: '1.7',
      status: 'active',
      lastUpdated: '2025-09-05',
      owner: 'Vendor Management',
      description: 'Framework for assessing and managing risks associated with third-party vendors.',
      effectiveDate: '2025-09-15',
      reviewDate: '2026-03-15',
      priority: 'high',
      tags: ['Vendor', 'Risk', 'Third-Party']
    },
    {
      id: 9,
      title: 'Business Continuity Plan',
      category: 'operations',
      version: '3.0',
      status: 'active',
      lastUpdated: '2025-08-30',
      owner: 'Operations Team',
      description: 'Strategies and procedures to maintain business operations during disruptions.',
      effectiveDate: '2025-09-10',
      reviewDate: '2026-03-10',
      priority: 'critical',
      tags: ['BCP', 'Continuity', 'Disaster Recovery']
    },
    {
      id: 10,
      title: 'Acceptable Use Policy',
      category: 'security',
      version: '4.2',
      status: 'active',
      lastUpdated: '2025-06-15',
      owner: 'IT Department',
      description: 'Guidelines for appropriate use of company technology resources and systems.',
      effectiveDate: '2025-07-01',
      reviewDate: '2026-01-01',
      priority: 'medium',
      tags: ['IT', 'Usage', 'Technology']
    },
    {
      id: 11,
      title: 'Change Management Protocol',
      category: 'operations',
      version: '2.1',
      status: 'active',
      lastUpdated: '2025-09-10',
      owner: 'Change Management Team',
      description: 'Standardized processes for implementing and managing organizational changes.',
      effectiveDate: '2025-09-20',
      reviewDate: '2026-03-20',
      priority: 'medium',
      tags: ['Change', 'Process', 'Management']
    },
    {
      id: 12,
      title: 'Whistleblower Protection Policy',
      category: 'ethics',
      version: '1.5',
      status: 'active',
      lastUpdated: '2025-08-01',
      owner: 'Legal & Ethics',
      description: 'Protection mechanisms for employees reporting unethical or illegal activities.',
      effectiveDate: '2025-08-15',
      reviewDate: '2026-08-15',
      priority: 'high',
      tags: ['Whistleblower', 'Ethics', 'Protection']
    },
    {
      id: 13,
      title: 'Quality Assurance Standards',
      category: 'operations',
      version: '3.8',
      status: 'active',
      lastUpdated: '2025-09-20',
      owner: 'Quality Team',
      description: 'Standards and procedures for maintaining product and service quality.',
      effectiveDate: '2025-10-01',
      reviewDate: '2026-04-01',
      priority: 'high',
      tags: ['Quality', 'Standards', 'QA']
    },
    {
      id: 14,
      title: 'Environmental Sustainability Policy',
      category: 'ethics',
      version: '2.0',
      status: 'active',
      lastUpdated: '2025-07-25',
      owner: 'Sustainability Team',
      description: 'Guidelines for environmental responsibility and sustainable business practices.',
      effectiveDate: '2025-08-10',
      reviewDate: '2026-08-10',
      priority: 'medium',
      tags: ['ESG', 'Environment', 'Sustainability']
    },
    {
      id: 15,
      title: 'Records Management Policy',
      category: 'data-privacy',
      version: '2.6',
      status: 'active',
      lastUpdated: '2025-06-30',
      owner: 'Records Management',
      description: 'Standards for creating, maintaining, and disposing of organizational records.',
      effectiveDate: '2025-07-15',
      reviewDate: '2026-01-15',
      priority: 'medium',
      tags: ['Records', 'Documentation', 'Management']
    },
    {
      id: 16,
      title: 'Cybersecurity Awareness Program',
      category: 'security',
      version: '1.4',
      status: 'active',
      lastUpdated: '2025-09-12',
      owner: 'Security Training',
      description: 'Program requirements for ongoing cybersecurity awareness and training.',
      effectiveDate: '2025-09-25',
      reviewDate: '2026-03-25',
      priority: 'high',
      tags: ['Training', 'Awareness', 'Cybersecurity']
    },
    {
      id: 17,
      title: 'Audit & Monitoring Standards',
      category: 'compliance',
      version: '3.3',
      status: 'active',
      lastUpdated: '2025-08-18',
      owner: 'Internal Audit',
      description: 'Standards for conducting internal audits and continuous monitoring activities.',
      effectiveDate: '2025-09-01',
      reviewDate: '2026-03-01',
      priority: 'high',
      tags: ['Audit', 'Monitoring', 'Compliance']
    },
    {
      id: 18,
      title: 'Risk Assessment Methodology',
      category: 'compliance',
      version: '2.9',
      status: 'active',
      lastUpdated: '2025-09-08',
      owner: 'Risk Management',
      description: 'Standardized methodology for identifying, assessing, and mitigating risks.',
      effectiveDate: '2025-09-20',
      reviewDate: '2026-03-20',
      priority: 'critical',
      tags: ['Risk', 'Assessment', 'Methodology']
    },
    {
      id: 19,
      title: 'Conflict of Interest Policy',
      category: 'ethics',
      version: '2.2',
      status: 'active',
      lastUpdated: '2025-07-05',
      owner: 'Ethics Committee',
      description: 'Guidelines for identifying and managing potential conflicts of interest.',
      effectiveDate: '2025-07-20',
      reviewDate: '2026-07-20',
      priority: 'high',
      tags: ['Ethics', 'Conflict', 'Disclosure']
    },
    {
      id: 20,
      title: 'Encryption Standards',
      category: 'data-privacy',
      version: '4.0',
      status: 'active',
      lastUpdated: '2025-09-25',
      owner: 'Security Architecture',
      description: 'Technical standards for data encryption at rest and in transit.',
      effectiveDate: '2025-10-05',
      reviewDate: '2026-04-05',
      priority: 'critical',
      tags: ['Encryption', 'Security', 'Technical']
    },
    {
      id: 21,
      title: 'Service Level Agreements',
      category: 'operations',
      version: '1.8',
      status: 'active',
      lastUpdated: '2025-08-10',
      owner: 'Operations Management',
      description: 'Standards for service delivery commitments and performance metrics.',
      effectiveDate: '2025-08-25',
      reviewDate: '2026-02-25',
      priority: 'medium',
      tags: ['SLA', 'Service', 'Performance']
    },
    {
      id: 22,
      title: 'Vulnerability Management',
      category: 'security',
      version: '2.5',
      status: 'active',
      lastUpdated: '2025-09-15',
      owner: 'Vulnerability Team',
      description: 'Process for identifying, prioritizing, and remediating security vulnerabilities.',
      effectiveDate: '2025-09-28',
      reviewDate: '2026-03-28',
      priority: 'critical',
      tags: ['Vulnerability', 'Patching', 'Security']
    },
    {
      id: 23,
      title: 'Privacy Impact Assessment',
      category: 'data-privacy',
      version: '1.6',
      status: 'active',
      lastUpdated: '2025-07-15',
      owner: 'Privacy Office',
      description: 'Framework for assessing privacy risks in new projects and initiatives.',
      effectiveDate: '2025-08-01',
      reviewDate: '2026-02-01',
      priority: 'high',
      tags: ['PIA', 'Privacy', 'Assessment']
    },
    {
      id: 24,
      title: 'Disaster Recovery Plan',
      category: 'operations',
      version: '3.4',
      status: 'active',
      lastUpdated: '2025-09-18',
      owner: 'DR Team',
      description: 'Procedures for recovering IT systems and data after catastrophic events.',
      effectiveDate: '2025-10-01',
      reviewDate: '2026-04-01',
      priority: 'critical',
      tags: ['DR', 'Recovery', 'Backup']
    },
    // ── Platform Policies · Canton Network ──────────────────────────────────
    // The Linux Foundation-aligned, in-repo evidence chain governing the Canton
    // settlement platform. Each maps to a committed artifact in the codebase.
    {
      id: 25,
      title: 'Open-Source License & Attribution (Apache-2.0)',
      category: 'platform',
      group: 'Canton Network',
      version: '1.0',
      status: 'active',
      lastUpdated: '2026-06-22',
      owner: 'Maintainers',
      description: 'OSI-approved Apache-2.0 license governing platform source, with a NOTICE that reserves KRO marks, client/settlement data, and secrets. Selected for compatibility with the Canton ecosystem (Splice / Daml SDK).',
      effectiveDate: '2026-06-22',
      reviewDate: '2026-12-22',
      priority: 'high',
      tags: ['Canton Network', 'Linux Foundation', 'Apache-2.0', 'Licensing'],
      documentRef: 'LICENSE · NOTICE',
      docFiles: [{ name: 'LICENSE', file: 'LICENSE.txt' }, { name: 'NOTICE', file: 'NOTICE.txt' }]
    },
    {
      id: 26,
      title: 'Contribution & Governance Policy',
      category: 'platform',
      group: 'Canton Network',
      version: '1.0',
      status: 'active',
      lastUpdated: '2026-06-22',
      owner: 'Maintainers',
      description: 'Governance model, branch strategy, contribution licensing (Apache-2.0 §5), determinism requirements, and the required CI checks that must pass before merge to main. No direct pushes to main.',
      effectiveDate: '2026-06-22',
      reviewDate: '2026-12-22',
      priority: 'high',
      tags: ['Canton Network', 'Governance', 'Contribution', 'Linux Foundation'],
      documentRef: 'CONTRIBUTING.md',
      docFiles: [{ name: 'CONTRIBUTING.md', file: 'CONTRIBUTING.md' }]
    },
    {
      id: 27,
      title: 'Code Ownership & Review Gates',
      category: 'platform',
      group: 'Canton Network',
      version: '1.0',
      status: 'active',
      lastUpdated: '2026-06-22',
      owner: 'Maintainers',
      description: 'Accountable owners and mandatory review gates per path (settlement engineering, contracts, platform/infra, security, risk, compliance). Combined with branch protection, no change to settlement logic lands without an accountable review.',
      effectiveDate: '2026-06-22',
      reviewDate: '2026-12-22',
      priority: 'high',
      tags: ['Canton Network', 'CODEOWNERS', 'Review Gates', 'Accountability'],
      documentRef: '.github/CODEOWNERS',
      docFiles: [{ name: 'CODEOWNERS', file: 'CODEOWNERS.txt' }]
    },
    {
      id: 28,
      title: 'Vulnerability Disclosure & Patching Policy',
      category: 'platform',
      group: 'Canton Network',
      version: '1.0',
      status: 'active',
      lastUpdated: '2026-06-22',
      owner: 'Security Team',
      description: 'Private disclosure channel, CVSS-based triage SLAs, and patching policy. Settlement-path defects (finality, double-spend, idempotency, custody, key management) are treated as Critical regardless of CVSS.',
      effectiveDate: '2026-06-22',
      reviewDate: '2026-12-22',
      priority: 'critical',
      tags: ['Canton Network', 'Security', 'Disclosure', 'Patching'],
      documentRef: 'SECURITY.md',
      docFiles: [{ name: 'SECURITY.md', file: 'SECURITY.md' }]
    },
    {
      id: 29,
      title: 'Architecture & Deterministic Behavior Standard',
      category: 'platform',
      group: 'Canton Network',
      version: '1.0',
      status: 'active',
      lastUpdated: '2026-06-22',
      owner: 'Settlement Engineering',
      description: 'System design and the core determinism invariant: deterministic anchors, SHA-256 canonical event hashing, fail-closed idempotency, and atomic DvP on the Global Synchronizer. Non-deterministic surfaces are explicitly captured-and-replayed (sandboxed).',
      effectiveDate: '2026-06-22',
      reviewDate: '2026-12-22',
      priority: 'critical',
      tags: ['Canton Network', 'Determinism', 'CIP-0056', 'Architecture'],
      documentRef: 'ARCHITECTURE.md',
      docFiles: [{ name: 'ARCHITECTURE.md', file: 'ARCHITECTURE.md' }]
    },
    {
      id: 30,
      title: 'Compliance Posture & Traceability Matrix',
      category: 'platform',
      group: 'Canton Network',
      version: '1.0',
      status: 'active',
      lastUpdated: '2026-06-22',
      owner: 'Compliance & Security',
      description: 'The traceability matrix mapping dependencies, libraries, smart-contract modules, external services, infrastructure, cryptographic primitives, and third-party integrations against version-pinning, license compatibility, security scanning, determinism, and documentation.',
      effectiveDate: '2026-06-22',
      reviewDate: '2026-09-22',
      priority: 'critical',
      tags: ['Canton Network', 'Linux Foundation', 'Traceability', 'Compliance'],
      documentRef: 'COMPLIANCE.md',
      docFiles: [{ name: 'COMPLIANCE.md', file: 'COMPLIANCE.md' }]
    },
    {
      id: 31,
      title: 'Supply-Chain Security Policy',
      category: 'platform',
      group: 'Canton Network',
      version: '1.0',
      status: 'active',
      lastUpdated: '2026-06-22',
      owner: 'Security Team',
      description: 'Continuous supply-chain controls: deterministic installs (npm ci from a committed lockfile), CodeQL SAST, Dependabot across all projects, and an npm audit gate that blocks on critical advisories with a license-compatibility check.',
      effectiveDate: '2026-06-22',
      reviewDate: '2026-09-22',
      priority: 'high',
      tags: ['Canton Network', 'Supply Chain', 'Dependabot', 'CodeQL', 'npm audit'],
      documentRef: '.github/workflows/supply-chain.yml · .github/dependabot.yml',
      docFiles: [{ name: 'supply-chain.yml', file: 'supply-chain.yml' }, { name: 'dependabot.yml', file: 'dependabot.yml' }]
    }
  ];

  const filteredPolicies = policies.filter(policy => {
    const matchesSearch = policy.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         policy.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         policy.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || policy.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getPriorityBadge = (priority) => {
    const badges = {
      critical: { color: 'danger', text: 'CRITICAL' },
      high: { color: 'warning', text: 'HIGH' },
      medium: { color: 'info', text: 'MEDIUM' },
      low: { color: 'secondary', text: 'LOW' }
    };
    return badges[priority] || badges.medium;
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { color: 'success', icon: '✓' },
      draft: { color: 'secondary', icon: '📝' },
      review: { color: 'warning', icon: '🔍' },
      archived: { color: 'dark', icon: '📦' }
    };
    return badges[status] || badges.active;
  };

  const handleViewPolicy = (policy) => {
    setSelectedPolicy(policy);
    setShowModal(true);
  };

  // Static governance docs are served from the app's public folder so they
  // preview without auth (repos are on private Azure DevOps).
  const docUrl = (file) => `${process.env.PUBLIC_URL || ''}/policies/canton-network/${file}`;

  const openDoc = (file) => {
    window.open(docUrl(file), '_blank', 'noopener,noreferrer');
  };

  const downloadDoc = (file, label) => {
    const a = document.createElement('a');
    a.href = docUrl(file);
    a.download = label || file;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDownload = (policy) => {
    // Platform policies map to real, committed artifacts — download the first.
    if (policy.docFiles && policy.docFiles.length) {
      downloadDoc(policy.docFiles[0].file, policy.docFiles[0].name);
      return;
    }
    console.log(`Downloading policy: ${policy.title}`);
    alert(`Downloading: ${policy.title} v${policy.version}`);
  };

  useEffect(() => {
    document.title = 'Policies - Compliance Tools - Bread Department';
  }, []);

  return (
    <div className="container-fluid py-4 policies-page">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb mb-2">
                  <li className="breadcrumb-item">
                    <Link to="/bread/compliance-tools">Compliance Tools</Link>
                  </li>
                  <li className="breadcrumb-item active">Policies</li>
                </ol>
              </nav>
              <h1 className="display-6 fw-bold mb-2">Policy Management</h1>
              <p className="text-muted mb-0">
                Centralized repository for organizational policies, standards, and procedures
              </p>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-primary btn-sm">
                <i className="bi bi-upload"></i> Request New Policy
              </button>
              <button className="btn btn-primary btn-sm">
                <i className="bi bi-plus-circle"></i> Create Policy
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="input-group">
                    <span className="input-group-text bg-white">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search policies by title, description, or tags..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setSearchTerm('')}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex gap-2 flex-wrap">
                    <select
                      className="form-select"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>
                          {cat.id === 'ethics' ? '' : `${cat.icon} `}{cat.name} ({cat.count})
                        </option>
                      ))}
                    </select>
                    <button className="btn btn-outline-secondary">
                      <i className="bi bi-funnel"></i> More Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category Cards */}
      <div className="row g-3 mb-4">
        {categories.slice(1).map(category => (
          <div key={category.id} className="col-md-6 col-lg-4 col-xl-2">
            <div
              className={`card shadow-sm border-0 h-100 category-card ${selectedCategory === category.id ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category.id)}
              style={{ cursor: 'pointer' }}
            >
              <div className="card-body text-center">
                <div className="category-icon mb-2">{renderCategoryIcon(category)}</div>
                <h6 className="card-title mb-1">{category.name}</h6>
                <span className="badge bg-primary">{category.count} policies</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Results Summary */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              {filteredPolicies.length} {filteredPolicies.length === 1 ? 'Policy' : 'Policies'} Found
            </h5>
            <div className="btn-group btn-group-sm">
              <button className="btn btn-outline-secondary active">
                <i className="bi bi-grid"></i> Grid
              </button>
              <button className="btn btn-outline-secondary">
                <i className="bi bi-list"></i> List
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Policy Cards Grid */}
      <div className="row g-3">
        {filteredPolicies.map(policy => {
          const priorityBadge = getPriorityBadge(policy.priority);
          const statusBadge = getStatusBadge(policy.status);
          
          return (
            <div key={policy.id} className="col-md-6 col-lg-4 col-xl-3">
              <div className="card policy-card shadow-sm border-0 h-100">
                <div className={`card-header bg-${priorityBadge.color} bg-opacity-10 border-0`}>
                  <div className="d-flex justify-content-between align-items-start">
                    <span className={`badge bg-${priorityBadge.color}`}>
                      {priorityBadge.text}
                    </span>
                    <span className={`badge bg-${statusBadge.color}`}>
                      {statusBadge.icon} {policy.status.toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="card-body">
                  <h6 className="card-title mb-2">{policy.title}</h6>
                  <p className="card-text text-muted small mb-3">
                    {policy.description.substring(0, 100)}...
                  </p>
                  <div className="policy-meta small mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted">Version:</span>
                      <span className="fw-semibold">{policy.version}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-muted">Owner:</span>
                      <span className="fw-semibold text-end">{policy.owner.split(' ')[0]}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="text-muted">Updated:</span>
                      <span className="fw-semibold">{new Date(policy.lastUpdated).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-1 mb-3">
                    {policy.tags.slice(0, 3).map((tag, idx) => (
                      <span key={idx} className="badge bg-light text-dark small">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="card-footer bg-white border-0 pt-0">
                  <div className="d-grid gap-2">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleViewPolicy(policy)}
                    >
                      <i className="bi bi-eye"></i> View Details
                    </button>
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => handleDownload(policy)}
                      >
                        <i className="bi bi-download"></i>
                      </button>
                      <button className="btn btn-outline-secondary">
                        <i className="bi bi-share"></i>
                      </button>
                      <button className="btn btn-outline-secondary">
                        <i className="bi bi-bookmark"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* No Results */}
      {filteredPolicies.length === 0 && (
        <div className="row mt-5">
          <div className="col-12">
            <div className="text-center py-5">
              <i className="bi bi-search display-1 text-muted"></i>
              <h4 className="mt-3">No policies found</h4>
              <p className="text-muted">Try adjusting your search or filter criteria</p>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Policy Detail Modal */}
      {showModal && selectedPolicy && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title">{selectedPolicy.title}</h5>
                  <small className="text-muted">Version {selectedPolicy.version}</small>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <div className="d-flex gap-2 mb-3">
                    <span className={`badge bg-${getPriorityBadge(selectedPolicy.priority).color}`}>
                      {getPriorityBadge(selectedPolicy.priority).text}
                    </span>
                    <span className={`badge bg-${getStatusBadge(selectedPolicy.status).color}`}>
                      {getStatusBadge(selectedPolicy.status).icon} {selectedPolicy.status.toUpperCase()}
                    </span>
                  </div>
                  <p className="lead">{selectedPolicy.description}</p>
                </div>

                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <div className="border rounded p-3">
                      <small className="text-muted d-block mb-1">Policy Owner</small>
                      <strong>{selectedPolicy.owner}</strong>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="border rounded p-3">
                      <small className="text-muted d-block mb-1">Category</small>
                      <strong>{categories.find(c => c.id === selectedPolicy.category)?.name}</strong>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="border rounded p-3">
                      <small className="text-muted d-block mb-1">Effective Date</small>
                      <strong>{new Date(selectedPolicy.effectiveDate).toLocaleDateString()}</strong>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="border rounded p-3">
                      <small className="text-muted d-block mb-1">Last Updated</small>
                      <strong>{new Date(selectedPolicy.lastUpdated).toLocaleDateString()}</strong>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="border rounded p-3">
                      <small className="text-muted d-block mb-1">Next Review</small>
                      <strong>{new Date(selectedPolicy.reviewDate).toLocaleDateString()}</strong>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <h6 className="mb-2">Tags</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {selectedPolicy.tags.map((tag, idx) => (
                      <span key={idx} className="badge bg-primary bg-opacity-10 text-primary">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {(selectedPolicy.documentRef || selectedPolicy.docFiles) && (
                  <div className="mb-3">
                    <h6 className="mb-2">Source of Record</h6>
                    <div className="border rounded p-3 bg-light">
                      {selectedPolicy.group && (
                        <small className="text-muted d-block mb-2">{selectedPolicy.group} · in-repo evidence</small>
                      )}
                      {selectedPolicy.docFiles ? (
                        <div className="d-flex flex-column gap-2">
                          {selectedPolicy.docFiles.map((doc, idx) => (
                            <div key={idx} className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                              <code className="text-dark">{doc.name}</code>
                              <div className="btn-group btn-group-sm">
                                <button className="btn btn-outline-primary" onClick={() => openDoc(doc.file)}>
                                  <i className="bi bi-box-arrow-up-right"></i> Open
                                </button>
                                <button className="btn btn-outline-secondary" onClick={() => downloadDoc(doc.file, doc.name)}>
                                  <i className="bi bi-download"></i> Download
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <code className="text-dark">{selectedPolicy.documentRef}</code>
                      )}
                    </div>
                  </div>
                )}

                <div className="alert alert-info">
                  <strong><i className="bi bi-info-circle"></i> Policy Information</strong>
                  <p className="mb-0 mt-2 small">
                    This policy is currently {selectedPolicy.status} and should be reviewed by all relevant stakeholders. 
                    For questions or clarifications, please contact the {selectedPolicy.owner}.
                  </p>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
                {selectedPolicy.docFiles ? (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => openDoc(selectedPolicy.docFiles[0].file)}
                  >
                    <i className="bi bi-box-arrow-up-right"></i> Open Document
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => handleDownload(selectedPolicy)}
                  >
                    <i className="bi bi-download"></i> Download PDF
                  </button>
                )}
                <button type="button" className="btn btn-primary">
                  <i className="bi bi-check-circle"></i> Acknowledge
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Policies;
